/**
 * useCoverArtBatch -- Unified cover art batch hook with per-category provider routing.
 *
 * Replaces direct useTmdbBatch calls across all streaming components.
 * Routes items to the correct provider (TMDB or TVDB) based on:
 * - Item type ("Movie" → movieProvider, "Series"/"Episode" → seriesProvider)
 * - Provider enabled state from CoverArtSettingsContext
 *
 * Both underlying hooks (useTmdbBatch, useTvdbBatch) are always called
 * to satisfy React's rules of hooks — empty arrays when not active.
 */

import { useMemo } from "react";

import { useCoverArtContext } from "../contexts/CoverArtSettingsContext";
import { useTmdbBatch, getTmdbPosterUrl, getTmdbBackdropUrl } from "./useTmdbBatch";
import { useTvdbBatch, getTvdbPosterUrl, getTvdbBackdropUrl } from "./useTvdbBatch";
import type { TmdbImageData } from "./useTmdbImages";
import type { TvdbImageData } from "./useTvdbImages";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CoverArtBatchItem {
  id: string;
  tmdbId: string | null | undefined;
  tvdbId: string | null | undefined;
  type: string;
}

/** Unified image data — same shape as TmdbImageData / TvdbImageData */
export type CoverArtImageData = TmdbImageData;

// ─────────────────────────────────────────────────────────────────────────────
// Provider Resolution
// ─────────────────────────────────────────────────────────────────────────────

export type CoverArtProvider = "tmdb" | "tvdb" | "none";

export interface CoverArtProviderConfig {
  movieProvider: CoverArtProvider;
  seriesProvider: CoverArtProvider;
  tmdbEnabled: boolean;
  tvdbEnabled: boolean;
}

/** The configured provider for an item type, before availability / id checks. */
function typeProvider(itemType: string, config: CoverArtProviderConfig): CoverArtProvider {
  if (itemType === "Movie") return config.movieProvider;
  if (itemType === "Series" || itemType === "Episode") return config.seriesProvider;
  return "none";
}

/**
 * Resolve the direct-art provider to actually fetch for an item.
 *
 * The item type's preferred provider wins when it is ENABLED and the item carries
 * that provider's id. Otherwise — the preferred provider is disabled, or the item
 * lacks its id — the OTHER enabled direct provider is used when the item has ITS
 * id. Failing both, `"none"`: the item falls back to the server (Emby/Jellyfin/Plex)
 * image. When the type's configured provider is `"none"` the admin chose server
 * art, so there is NO fallback to the other provider.
 *
 * This is why so few Intelligence covers converted before: only ~11% of cached
 * items carry a `tvdb_id`, so a tvdb-preferred series with only a `tmdb_id` was
 * dropped straight to the Emby image instead of using its available tmdb art.
 */
export function resolveItemProvider(
  itemType: string,
  hasTmdbId: boolean,
  hasTvdbId: boolean,
  config: CoverArtProviderConfig,
): CoverArtProvider {
  const preferred = typeProvider(itemType, config);
  if (preferred === "none") return "none";

  const canTmdb = config.tmdbEnabled && hasTmdbId;
  const canTvdb = config.tvdbEnabled && hasTvdbId;

  if (preferred === "tmdb") {
    if (canTmdb) return "tmdb";
    if (canTvdb) return "tvdb";
    return "none";
  }
  // preferred === "tvdb"
  if (canTvdb) return "tvdb";
  if (canTmdb) return "tmdb";
  return "none";
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useCoverArtBatch(
  items: CoverArtBatchItem[],
): Map<string, CoverArtImageData> {
  const { movieProvider, seriesProvider, tmdbEnabled, tvdbEnabled } = useCoverArtContext();

  // Split items by effective provider
  const { tmdbItems, tvdbItems } = useMemo(() => {
    const tmdb: Array<{ id: string; tmdbId: string | null | undefined; type: string }> = [];
    const tvdb: Array<{ id: string; tvdbId: string | null | undefined; type: string }> = [];

    const config: CoverArtProviderConfig = { movieProvider, seriesProvider, tmdbEnabled, tvdbEnabled };
    for (const item of items) {
      const provider = resolveItemProvider(item.type, Boolean(item.tmdbId), Boolean(item.tvdbId), config);
      if (provider === "tmdb") {
        tmdb.push({ id: item.id, tmdbId: item.tmdbId, type: item.type });
      } else if (provider === "tvdb") {
        tvdb.push({ id: item.id, tvdbId: item.tvdbId, type: item.type });
      }
    }
    return { tmdbItems: tmdb, tvdbItems: tvdb };
  }, [items, movieProvider, seriesProvider, tmdbEnabled, tvdbEnabled]);

  // Always call both hooks (React rules) — empty arrays when not needed
  const tmdbMap = useTmdbBatch(tmdbItems);
  const tvdbMap = useTvdbBatch(tvdbItems);

  // Merge results into single Map
  return useMemo(() => {
    if (tmdbMap.size === 0 && tvdbMap.size === 0) return new Map<string, CoverArtImageData>();
    const merged = new Map<string, CoverArtImageData>();
    for (const [k, v] of tmdbMap) merged.set(k, v);
    for (const [k, v] of tvdbMap) merged.set(k, v);
    return merged;
  }, [tmdbMap, tvdbMap]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Unified URL Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get a cover art poster URL from a unified map.
 * Detects the provider by file path format:
 * - TMDB paths start with "/" (e.g. "/abc123.jpg")
 * - TVDB paths are relative (e.g. "posters/121361-5.jpg")
 */
export function getCoverArtPosterUrl(
  map: Map<string, CoverArtImageData>,
  itemId: string,
  maxWidth = 400,
): string | null {
  const entry = map.get(itemId);
  if (!entry?.bestPoster) return null;
  if (entry.bestPoster.filePath.startsWith("/")) {
    return getTmdbPosterUrl(map as Map<string, TmdbImageData>, itemId, maxWidth);
  }
  return getTvdbPosterUrl(map as Map<string, TvdbImageData>, itemId);
}

/**
 * Get a cover art backdrop URL from a unified map.
 */
export function getCoverArtBackdropUrl(
  map: Map<string, CoverArtImageData>,
  itemId: string,
  maxWidth = 1920,
): string | null {
  const entry = map.get(itemId);
  if (!entry?.bestBackdrop) return null;
  if (entry.bestBackdrop.filePath.startsWith("/")) {
    return getTmdbBackdropUrl(map as Map<string, TmdbImageData>, itemId, maxWidth);
  }
  return getTvdbBackdropUrl(map as Map<string, TvdbImageData>, itemId);
}
